import { useCallback, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useLingui } from '@lingui/react/macro';
import { cn } from '@worldcoo/ui/lib/utils';
import { Carousel, CarouselItem } from '@worldcoo/ui/components/carousel';
import { DiamondGrid, diamondGlows } from '../primitives';
import { DigitRoll } from '../digit-roll';
import { useDragScroll } from '../use-drag-scroll';
import { useMediaQuery } from '../use-media-query';
import { useMilestones } from './milestones-data';
import { MilestoneCard } from './milestone-card';

/*
 * Slot geometry lives in the classes below rather than in constants, because it has to change with the
 * breakpoint and Tailwind is what knows the breakpoint:
 *
 *   - a SLIDE is the distance between two milestones, not the width of a card: 843px at `xl` (centre of
 *     the canvas to centre of a side frame, from the export's `left: -344px`) with the 442px card
 *     centred in it, and 69% of the viewport plus a 2rem gap below that. Giving the slide the whole step
 *     is what lets the snap points be the years themselves.
 *   - the first and last slides carry a margin of half the leftover width, which is what lets 2012 and
 *     2026 reach the centre of the track. Margins rather than the track's own padding: a percentage
 *     width on a slide resolves against the track's CONTENT box, so padding there would quietly shrink
 *     every slide by the amount it added.
 */

/**
 * One cell of the year odometer. `leading-none` on a cell whose height is the figure's ORIGINAL line box
 * (80px and 168px on the 1.3 leading the rest of this section uses) is what keeps the numeral where the
 * plain paragraph put it while giving the roll ten cells of exactly equal height to count in.
 */
const YEAR_CELL =
	'flex h-[6.5rem] items-center justify-center text-[5rem] leading-none font-medium tracking-[-0.02em] xl:h-[13.65rem] xl:text-[10.5rem]';

/**
 * How far a snap group's centre is from the track's — the one measurement both the jump and the
 * read-back need, so they cannot disagree about where a stop is.
 *
 * MEASURED, not multiplied: a group is one slide wide or several, so there is no uniform step left to
 * multiply an index by. Rects rather than `offsetLeft` because they are already in the track's own
 * frame, and they describe the position the reader can see — during a smooth scroll they and
 * `scrollLeft` move together, so the absolute target the caller computes from the two stays right.
 */
function groupOffset(el: HTMLUListElement, group: number[]) {
	const slides = group.map((i) => el.children.item(i)).filter((child) => child instanceof HTMLElement);
	const first = slides[0];
	const last = slides[slides.length - 1];
	if (!first || !last) return null;
	const track = el.getBoundingClientRect();
	return (first.getBoundingClientRect().left + last.getBoundingClientRect().right) / 2 - (track.left + track.right) / 2;
}

/** The rail's marker: an 8px brand disc with a 4px hole, which the hand-off draws as two stacked circles. */
function YearMarker() {
	return (
		<span aria-hidden className="bg-brand relative block size-2 rounded-full">
			<span className="absolute top-1/2 left-1/2 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white" />
		</span>
	);
}

/**
 * "Nuestros hitos" — the timeline carousel.
 *
 * Three cards are on screen at once: the active one centred at full size, and the previous and next
 * bleeding off either edge at 25%. The year rail bottom-right is the control, and the active year is
 * repeated as a 168px figure bottom-left.
 *
 * The track is the shared `Carousel`, a NATIVE scroll-snap list: a slide is the whole 843px step with
 * the card centred in it, so the snap points are the years themselves and the swipe, the momentum, the
 * keyboard and the trackpad all come from the browser instead of from a drag gesture we animate. Every
 * milestone is a real, reachable list item — the previous spelling kept them all stacked at one point
 * and moved them with a transform, which is why the far ones had to be hidden at `opacity: 0`.
 *
 * WHICH MAKES THE SCROLL POSITION THE STATE. `active` is read back off it rather than set alongside it,
 * so the rail and the year figure say what is actually on screen however it got there — and every
 * control moves the TRACK and lets that read-back follow, rather than moving an index the next scroll
 * event would overwrite.
 *
 * THE PAGE SCROLL IS ONE OF THOSE CONTROLS. The section pins itself for one viewport plus three quarters
 * of one per milestone, and scrolling down through that turns into lateral movement here: the years go
 * past, and the page carries on below once 2026 is reached. It drives the track by scrolling it — one
 * more caller of the same `showMilestone` the rail uses, and it SKIPS the same way the rail does, so a
 * space bar that covers three years lands on the third — rather than by writing `scrollLeft` every
 * frame, which would fight the snapping the browser is doing at the same time.
 */
/**
 * The year the timeline opens on. Named rather than an index, so adding a milestone cannot move it
 * silently — but it is still fixed, and 2026 is already in the data.
 */
const OPENS_ON = '2025';

export function Milestones() {
	const { t } = useLingui();
	const milestones = useMilestones();
	const [active, setActive] = useState(() =>
		Math.max(
			0,
			milestones.findIndex((m) => m.year === OPENS_ON),
		),
	);
	const track = useRef<HTMLUListElement>(null);
	const current = milestones[active];

	/**
	 * The rail is a YEAR AXIS, not one entry per slide: 2025 holds two milestones — the donor app and
	 * the first success story in Italy — and the design labels that year once. So the years are deduped,
	 * a click lands on the FIRST slide of its year, and the marker follows the active slide's year.
	 *
	 * `count` is what the layout below spends: a year of several milestones is drawn as those cards side
	 * by side, sharing one snap point, so its slides are card-width and its lead slide's snap area covers
	 * the whole group.
	 */
	const years = milestones.reduce<{ year: string; first: number; count: number }[]>((acc, milestone, i) => {
		const entry = acc.find((candidate) => candidate.year === milestone.year);
		if (entry) entry.count += 1;
		else acc.push({ year: milestone.year, first: i, count: 1 });
		return acc;
	}, []);
	const activeYear = years.findIndex((entry) => entry.year === current?.year);

	/**
	 * WHERE THE SNAP POINTS ARE, as groups of slide indices — which is not one per slide, because at `xl`
	 * a year drawn as two cards is one place the track can stop. Below `xl` the cards would each need a
	 * whole viewport, so there the two are separate swipes and every slide is its own group.
	 *
	 * Read from `matchMedia` rather than from the DOM: the two halves of the pairing are CSS
	 * (`carousel-group-follow` / `carousel-group-lead` in globals.css), so this and that media query have
	 * to name the same breakpoint or the read-back would look for stops the browser will not make.
	 */
	const paired = useMediaQuery('(min-width: 1280px)');
	const yearOf = milestones.map((milestone) => milestone.year).join(',');
	const snapGroups = useMemo(() => {
		const list = yearOf.split(',');
		if (!paired) return list.map((_, i) => [i]);
		return list.reduce<number[][]>((acc, year, i) => {
			const group = acc[acc.length - 1];
			if (group && list[i - 1] === year) group.push(i);
			else acc.push([i]);
			return acc;
		}, []);
	}, [paired, yearOf]);

	/**
	 * The jump behind every control: the rail, a click on a neighbour, and the page's own scroll.
	 *
	 * ONE `scrollTo` STRAIGHT TO THE TARGET, so a jump of several years is a jump. This used to walk the
	 * track a step at a time so that each year in between lit up in the rail, and the walk was wrong twice
	 * over: it depended on the browser reporting a rounded position AT every intermediate snap point,
	 * which a fast page scroll (a space bar, a trackpad fling) glides past without ever landing on — 2018
	 * was the year that vanished — and it fought the smooth scroll it had just started, since re-issuing
	 * `scrollTo` mid-glide restarts the ease over the shrinking remainder. Skipping is what the rail has
	 * always done, and it is the honest reading of the gesture: someone who scrolled past three years did
	 * not ask to watch three years.
	 *
	 * `behavior` is left unset on purpose: `scrollTo` then defers to the track's own `scroll-behavior`,
	 * which the carousel already makes smooth only when motion is not being reduced.
	 */
	const showMilestone = useCallback(
		(index: number) => {
			const el = track.current;
			if (!el) return;
			const group = snapGroups.find((candidate) => candidate.includes(index));
			const offset = group && groupOffset(el, group);
			if (offset !== undefined && offset !== null) el.scrollTo({ left: el.scrollLeft + offset });
		},
		[snapGroups],
	);

	/**
	 * Where the timeline opens, and it does NOT move with the page. This was pinned and walked by the
	 * scroll, which took eight screens to say what a click says — a reader who wanted 2014 had to scroll
	 * back UP through the section. The pin, its extra height and the step-follows-scroll effect are gone.
	 */
	const opensOn = Math.max(
		0,
		milestones.findIndex((milestone) => milestone.year === OPENS_ON),
	);

	/**
	 * Placing the opening year is a scroll offset, so it is done in the ref rather than in an effect —
	 * which is what keeps the first paint from showing 2012 and sliding off it. Once only: `snapGroups`
	 * changes when the `xl` pairing resolves, and re-running would drag a reader who had already swiped.
	 */
	const placed = useRef(false);
	const attach = useCallback(
		(el: HTMLUListElement | null) => {
			track.current = el;
			if (!el || placed.current) return;
			const group = snapGroups.find((candidate) => candidate.includes(opensOn));
			const offset = group && groupOffset(el, group);
			if (offset === undefined || offset === null) return;
			el.scrollTo({ left: el.scrollLeft + offset, behavior: 'instant' });
			placed.current = true;
		},
		[snapGroups, opensOn],
	);

	/**
	 * The scroll position IS the state: `active` is read back off it rather than set alongside it.
	 *
	 * The nearest snap group rather than a rounded `scrollLeft / step`: with a year's cards sharing one
	 * stop the slides are no longer all one width, so there is no step to divide by. `active` is that
	 * group's FIRST slide, which is what makes a paired year read as one place on the timeline.
	 */
	const nearestStop = useCallback(() => {
		const el = track.current;
		if (!el) return null;
		let nearest = 0;
		let shortest = Infinity;
		for (const group of snapGroups) {
			const offset = groupOffset(el, group);
			if (offset === null || Math.abs(offset) >= shortest) continue;
			shortest = Math.abs(offset);
			nearest = group[0] ?? 0;
		}
		return nearest;
	}, [snapGroups]);

	const onScroll = () => {
		const nearest = nearestStop();
		if (nearest !== null) setActive(nearest);
	};

	/** Where a drag lands: `scroll-snap-type` does not re-snap on its own. Same jump the rail uses. */
	const settleDrag = useCallback(() => {
		const nearest = nearestStop();
		if (nearest !== null) showMilestone(nearest);
	}, [nearestStop, showMilestone]);

	// Grab-and-pull with a mouse; a touch keeps the native scroll. See the hook for the three
	// non-obvious parts.
	const dragProps = useDragScroll<HTMLUListElement>({ onDragEnd: settleDrag });

	return (
		<section className="relative bg-white">
			<div className="flex min-h-screen items-center overflow-hidden">
				{/* Brand-orange texture in the top-right corner. The export's glow is a 777x501 ellipse whose
				    right edge sits 339px past the canvas edge and whose top is at -314px, so its centre lands at
				    (1390.5, -63.5) — expressed from the RIGHT edge here so it stays in the corner at any width. */}
				<DiamondGrid
					color="var(--color-brand)"
					opacity={0.95}
					masks={[diamondGlows({ x: 'calc(100% - 50px)', y: '-63px' })]}
				/>

				{/* No horizontal padding below `xl`: the mobile hand-off insets each block differently — the
				    heading and the card at 16% of the width, the year and the rail near the edge — and the card
				    HAS to be narrower than the viewport for the neighbours to show at both edges. */}
				<div className="relative mx-auto flex w-full max-w-[1440px] flex-col gap-10 py-16 md:py-20 xl:h-[738px] xl:gap-0 xl:py-0">
					{/* Left-aligned and flush with the card on mobile; centred at the top of the band on desktop.
					    36px on mobile against the desktop 40: measured by normalising one heading's line width
					    against the capture's own content width, which is the only scale-free way to read it off a
					    screenshot whose zoom is unknown. Every section heading uses the same pair. */}
					<h2 className="text-ink ml-[16%] text-4xl leading-[1.3] font-medium tracking-[-0.02em] md:text-[2.5rem] xl:absolute xl:top-[65px] xl:ml-0 xl:w-full xl:text-center">
						{t`Our milestones`}
					</h2>

					{/* The carousel's own floating scroll buttons are off: the year rail below is this carousel's
					    control, and the hand-off draws no arrows. `px-0` undoes the shared inset, which would
					    otherwise be subtracted from the width every slide is a percentage of.

					    The track BREAKS OUT of the 1440px band it sits in — `w-screen` centred on the viewport rather
					    than `inset-x-0` — because the track is the thing that clips: `.carousel` is the `overflow-x: auto`
					    box. Held to 1440 it cut the previous and next cards off in mid-card, with white bands beside them,
					    on any wider screen, instead of letting them bleed off the viewport's own edges the way the design
					    draws. Nothing else has to move: the end slides' margins are `calc(50% - 421.5px)`, so half of a
					    wider track is still the centre the active card already sat on. */}
					<Carousel
						ref={attach}
						{...dragProps}
						onScroll={onScroll}
						aria-label={t`Our milestones`}
						className="carousel-no-buttons gap-8 px-0 select-none xl:absolute xl:top-[182px] xl:left-1/2 xl:w-screen xl:-translate-x-1/2 xl:gap-0"
					>
						{milestones.map((milestone, i) => {
							const yearIndex = years.findIndex((entry) => entry.year === milestone.year);
							const group = years[yearIndex];
							const count = group?.count ?? 1;
							return (
								<CarouselItem
									key={milestone.id}
									// The extra width the lead slide's snap area has to cover, in the group's own card widths —
									// see `carousel-group-lead` in globals.css, which spends it only at `xl`.
									style={{ '--carousel-group-extra': `${(count - 1) * 474}px` } as CSSProperties}
									// 69% of the viewport with half the remainder either side, which is what the neighbours
									// show through; one whole 843px step at `xl`, with the card centred inside it — except in a
									// year the design draws as several cards, where each slide is just a card and its 32px gap
									// (442 + 32) and the group as a whole takes the room a single slide would.
									//
									// `snap-always` is one stop at a time for the gestures the BROWSER owns: scroll-snap-stop
									// forbids a fling from carrying past a snap point, so a hard swipe advances one year rather
									// than four. It does not constrain a programmatic scroll, which is what leaves the rail and the
									// page scroll free to jump from 2012 to 2026.
									className={cn(
										'flex w-[69%] snap-always justify-center first:ml-[15.5%] last:mr-[15.5%] xl:first:ml-[calc(50%-421.5px)] xl:last:mr-[calc(50%-421.5px)]',
										count > 1 ? 'xl:w-[474px]' : 'xl:w-[843px]',
										// The two halves of the pairing, both of them `xl`-only and both in globals.css: the year's
										// first slide carries the group's whole snap area, the rest stop being snap targets at all.
										count > 1 && (i === group?.first ? 'carousel-group-lead' : 'carousel-group-follow'),
									)}
								>
									<MilestoneCard
										milestone={milestone}
										isFocused={i === active}
										inFocusedYear={milestone.year === current?.year}
										yearOffset={yearIndex - activeYear}
										onSelect={() => showMilestone(i)}
									/>
								</CarouselItem>
							);
						})}
					</Carousel>

					{/* The active year, repeated large — and ROLLED rather than swapped, on the same odometer the
					    Solutions counter uses: a year is four digits of which usually only the last two move, so a
					    column of 0-9 rolling past the digits in between says "time passing" in a way a crossfade
					    cannot. `aria-hidden` because it only restates the year the rail below already marks. */}
					<div aria-hidden className="text-brand ml-5 flex xl:absolute xl:top-[539px] xl:left-8 xl:ml-0">
						{/* Keyed by POSITION, not by digit: the thousands column reads `2` on every milestone and has
						    to stay the SAME element across a change, or React would swap it for a new one and the
						    roll would start over from zero instead of carrying on from where it was. */}
						{(current?.year ?? '').split('').map((digit, i) => (
							// oxlint-disable-next-line react/no-array-index-key
							<DigitRoll key={i} digit={Number(digit)} cell={YEAR_CELL} />
						))}
					</div>

					{/* The rail. A grid rather than two rows of flex, so the marker sits in the active year's own
					    column and needs no measuring: `justify-self-center` puts it under that year whatever the
					    numeral's width. The line is one more grid item spanning every column. */}
					<div
						// 16px years on a 12px gap at BOTH sizes — measured ink-for-ink against the desktop capture,
						// the rail is not scaled down for mobile. Its 326px total is exactly what a 375px screen
						// leaves inside these gutters.
						className="ml-9 grid gap-x-3 self-start xl:absolute xl:right-8 xl:bottom-8 xl:ml-0 xl:self-auto"
						style={{ gridTemplateColumns: `repeat(${years.length}, auto)` }}
					>
						{years.map(({ year, first }, i) => (
							<button
								key={year}
								type="button"
								onClick={() => showMilestone(first)}
								aria-current={i === activeYear ? 'true' : undefined}
								style={{ gridRow: 1, gridColumn: i + 1 }}
								className={cn(
									'text-ink cursor-pointer text-base leading-[1.3] font-medium tracking-[-0.02em] transition-opacity',
									i === activeYear ? 'opacity-100' : 'opacity-50 hover:opacity-75',
								)}
							>
								{year}
							</button>
						))}
						<span
							aria-hidden
							style={{ gridRow: 2, gridColumn: '1 / -1' }}
							className="bg-brand/30 mt-[9px] h-px self-center"
						/>
						<span
							aria-hidden
							style={{ gridRow: 2, gridColumn: activeYear + 1 }}
							className="mt-[9px] self-center justify-self-center"
						>
							<YearMarker />
						</span>
					</div>
				</div>
			</div>
		</section>
	);
}
